import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Plus, Trash2, Pencil, Check, X, Users } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPost, apiPut, apiDelete } from '../../api/client';
import { Button } from '../../components/ui/Button';
import type { CollaboratorTemplate, CollaboratorTemplateTeam, CollaboratorTemplateRole, EventType } from '../../api/types';

const EVENT_TYPE_LABELS: Record<EventType | 'ALL', string> = {
  ALL: 'Tutti gli eventi',
  MATRIMONIO: 'Matrimonio', BATTESIMO: 'Battesimo', COMUNIONE: 'Comunione',
  CRESIMA: 'Cresima', COMPLEANNO: 'Compleanno', RITRATTO: 'Ritratto', ALTRO: 'Altro',
};
const EVENT_TYPES: Array<EventType | ''> = ['', 'MATRIMONIO', 'BATTESIMO', 'COMUNIONE', 'CRESIMA', 'COMPLEANNO', 'RITRATTO', 'ALTRO'];

const inputStyle: React.CSSProperties = {
  background: 'var(--glass-bg-card)',
  border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 'var(--radius-card)',
  color: 'var(--color-text)',
  fontSize: 'var(--text-sm)',
  padding: '0.5rem 0.75rem',
  outline: 'none',
  width: '100%',
};

const selectStyle: React.CSSProperties = { ...inputStyle, cursor: 'pointer' };

// ─── RoleRow ──────────────────────────────────────────────────────────────────

function RoleRow({
  templateId, teamId, role,
}: {
  templateId: string;
  teamId: string;
  role: CollaboratorTemplateRole;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ role: role.role, defaultFee: String(role.defaultFee), isRequired: role.isRequired });

  const updateMutation = useMutation({
    mutationFn: () => apiPut(`/collaborator-templates/${templateId}/teams/${teamId}/roles/${role.id}`, {
      role: form.role || undefined,
      defaultFee: form.defaultFee !== '' ? Number(form.defaultFee) : undefined,
      isRequired: form.isRequired,
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['collaborator-templates'] }); setEditing(false); toast.success('Ruolo aggiornato'); },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiDelete(`/collaborator-templates/${templateId}/teams/${teamId}/roles/${role.id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['collaborator-templates'] }),
    onError: (e: Error) => toast.error(e.message),
  });

  if (editing) {
    return (
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', background: 'var(--glass-bg-card)', borderRadius: 'var(--radius-interactive)', padding: '0.4rem 0.6rem' }}>
        <input style={{ ...inputStyle, flex: 2, minWidth: '8rem' }} value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} placeholder="Ruolo" autoFocus />
        <input type="number" min="0" style={{ ...inputStyle, width: '7rem', flex: 'none' }} value={form.defaultFee} onChange={e => setForm(f => ({ ...f, defaultFee: e.target.value }))} placeholder="€" />
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: 'var(--text-sm)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
          <input type="checkbox" checked={form.isRequired} onChange={e => setForm(f => ({ ...f, isRequired: e.target.checked }))} />
          Richiesto
        </label>
        <button onClick={() => updateMutation.mutate()} disabled={!form.role.trim()} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-success)' }}><Check size={14} /></button>
        <button onClick={() => { setEditing(false); setForm({ role: role.role, defaultFee: String(role.defaultFee), isRequired: role.isRequired }); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)' }}><X size={14} /></button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.35rem 0.6rem', background: 'var(--glass-bg-card)', borderRadius: 'var(--radius-interactive)' }}>
      <span style={{ flex: 1, fontSize: 'var(--text-sm)', fontWeight: 500 }}>{role.role}</span>
      <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>{role.defaultFee > 0 ? `€ ${role.defaultFee.toFixed(0)}` : '—'}</span>
      {role.isRequired && (
        <span style={{ fontSize: 'var(--text-xs)', background: 'var(--color-primary-subtle)', color: 'var(--color-primary)', borderRadius: 'var(--radius-interactive)', padding: '0.1rem 0.35rem', fontWeight: 600 }}>
          Richiesto
        </span>
      )}
      <button onClick={() => setEditing(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', padding: '0.15rem' }}><Pencil size={12} /></button>
      <button onClick={() => deleteMutation.mutate()} disabled={deleteMutation.isPending} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)', padding: '0.15rem' }}><Trash2 size={12} /></button>
    </div>
  );
}

// ─── TeamSection ─────────────────────────────────────────────────────────────

function TeamSection({
  template, team, multiTeam,
}: {
  template: CollaboratorTemplate;
  team: CollaboratorTemplateTeam;
  multiTeam: boolean;
}) {
  const qc = useQueryClient();
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(team.name ?? '');
  const [showAddRole, setShowAddRole] = useState(false);
  const [newRole, setNewRole] = useState({ role: '', defaultFee: '', isRequired: false });

  const updateTeam = useMutation({
    mutationFn: () => apiPut(`/collaborator-templates/${template.id}/teams/${team.id}`, { name: nameInput || null }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['collaborator-templates'] }); setEditingName(false); },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteTeam = useMutation({
    mutationFn: () => apiDelete(`/collaborator-templates/${template.id}/teams/${team.id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['collaborator-templates'] }),
    onError: (e: Error) => toast.error(e.message),
  });

  const addRole = useMutation({
    mutationFn: () => apiPost(`/collaborator-templates/${template.id}/teams/${team.id}/roles`, {
      role: newRole.role,
      defaultFee: newRole.defaultFee !== '' ? Number(newRole.defaultFee) : 0,
      isRequired: newRole.isRequired,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['collaborator-templates'] });
      setNewRole({ role: '', defaultFee: '', isRequired: false });
      setShowAddRole(false);
      toast.success('Ruolo aggiunto');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div style={{ border: '1px solid rgba(255,255,255,0.08)', borderRadius: 'var(--radius-card)', overflow: 'hidden' }}>
      {/* Team header */}
      <div style={{ background: 'var(--color-surface-3)', padding: '0.4rem 0.75rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <Users size={12} style={{ color: 'var(--color-text-faint)', flexShrink: 0 }} />
        {multiTeam ? (
          editingName ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flex: 1 }}>
              <input
                value={nameInput}
                onChange={e => setNameInput(e.target.value)}
                placeholder="Nome squadra nel template…"
                autoFocus
                style={{ ...inputStyle, padding: '0.2rem 0.4rem' }}
              />
              <button onClick={() => updateTeam.mutate()} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-success)' }}><Check size={13} /></button>
              <button onClick={() => { setEditingName(false); setNameInput(team.name ?? ''); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-faint)' }}><X size={13} /></button>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flex: 1 }}>
              <span style={{ fontSize: 'var(--text-sm)', fontWeight: 500 }}>
                {team.name || <span style={{ color: 'var(--color-text-faint)', fontStyle: 'italic' }}>Squadra senza nome</span>}
              </span>
              <button onClick={() => setEditingName(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-faint)', padding: '0.1rem' }}><Pencil size={11} /></button>
            </div>
          )
        ) : (
          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', flex: 1 }}>Squadra</span>
        )}
        <button
          onClick={() => { if (confirm('Eliminare questa squadra e tutti i suoi ruoli?')) deleteTeam.mutate(); }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)', padding: '0.1rem' }}
          title="Elimina squadra"
        >
          <Trash2 size={12} />
        </button>
      </div>

      {/* Roles */}
      <div style={{ padding: '0.5rem 0.75rem', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
        {team.roles.map(r => (
          <RoleRow key={r.id} templateId={template.id} teamId={team.id} role={r} />
        ))}

        {showAddRole ? (
          <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap', background: 'var(--glass-bg-card)', borderRadius: 'var(--radius-interactive)', padding: '0.4rem 0.5rem', border: '1px dashed rgba(255,255,255,0.10)' }}>
            <input
              style={{ ...inputStyle, flex: 2, minWidth: '8rem' }}
              value={newRole.role}
              onChange={e => setNewRole(r => ({ ...r, role: e.target.value }))}
              placeholder="Nome ruolo"
              autoFocus
              onKeyDown={e => { if (e.key === 'Enter' && newRole.role.trim()) addRole.mutate(); }}
            />
            <input type="number" min="0" style={{ ...inputStyle, width: '7rem', flex: 'none' }} value={newRole.defaultFee} onChange={e => setNewRole(r => ({ ...r, defaultFee: e.target.value }))} placeholder="€" />
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.2rem', fontSize: 'var(--text-sm)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={newRole.isRequired} onChange={e => setNewRole(r => ({ ...r, isRequired: e.target.checked }))} /> Richiesto
            </label>
            <button onClick={() => addRole.mutate()} disabled={!newRole.role.trim()} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-success)' }}><Check size={14} /></button>
            <button onClick={() => { setShowAddRole(false); setNewRole({ role: '', defaultFee: '', isRequired: false }); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)' }}><X size={14} /></button>
          </div>
        ) : (
          <button
            onClick={() => setShowAddRole(true)}
            style={{ background: 'none', border: '1px dashed rgba(255,255,255,0.10)', borderRadius: 'var(--radius-interactive)', cursor: 'pointer', color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)', padding: '0.3rem 0.6rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}
          >
            <Plus size={12} /> Aggiungi ruolo
          </button>
        )}
      </div>
    </div>
  );
}

// ─── TemplateCard ─────────────────────────────────────────────────────────────

function TemplateCard({ template }: { template: CollaboratorTemplate }) {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameForm, setNameForm] = useState({ name: template.name, eventType: template.eventType ?? ('' as EventType | '') });

  const updateMutation = useMutation({
    mutationFn: () => apiPut(`/collaborator-templates/${template.id}`, { name: nameForm.name || undefined, eventType: nameForm.eventType || null }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['collaborator-templates'] }); setEditingName(false); toast.success('Template aggiornato'); },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiDelete(`/collaborator-templates/${template.id}`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['collaborator-templates'] }); toast.success('Template eliminato'); },
    onError: (e: Error) => toast.error(e.message),
  });

  const addTeam = useMutation({
    mutationFn: () => apiPost(`/collaborator-templates/${template.id}/teams`, { name: null }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['collaborator-templates'] }),
    onError: (e: Error) => toast.error(e.message),
  });

  const totalRoles = template.teams.reduce((s, t) => s + t.roles.length, 0);
  const eventLabel = template.eventType ? EVENT_TYPE_LABELS[template.eventType] : 'Tutti gli eventi';
  const multiTeam = template.teams.length > 1;

  return (
    <div style={{ border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)', background: 'var(--glass-bg-card)', overflow: 'hidden' }}>
      {/* Header */}
      <div
        style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.875rem 1rem', cursor: 'pointer', background: expanded ? 'var(--glass-bg-card)' : 'transparent' }}
        onClick={() => setExpanded(e => !e)}
      >
        {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}

        {editingName ? (
          <div style={{ flex: 1, display: 'flex', gap: '0.5rem', alignItems: 'center' }} onClick={e => e.stopPropagation()}>
            <input style={{ ...inputStyle, flex: 2 }} value={nameForm.name} onChange={e => setNameForm(f => ({ ...f, name: e.target.value }))} autoFocus />
            <select style={{ ...selectStyle, width: 'auto' }} value={nameForm.eventType} onChange={e => setNameForm(f => ({ ...f, eventType: e.target.value as EventType | '' }))}>
              {EVENT_TYPES.map(t => <option key={t} value={t}>{t ? EVENT_TYPE_LABELS[t] : 'Tutti gli eventi'}</option>)}
            </select>
            <button onClick={() => updateMutation.mutate()} disabled={!nameForm.name.trim()} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-success)' }}><Check size={16} /></button>
            <button onClick={() => { setEditingName(false); setNameForm({ name: template.name, eventType: template.eventType ?? '' }); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)' }}><X size={16} /></button>
          </div>
        ) : (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
            <span style={{ fontWeight: 600, fontSize: 'var(--text-sm)' }}>{template.name}</span>
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', background: 'var(--color-surface-3)', borderRadius: 'var(--radius-interactive)', padding: '0.125rem 0.5rem' }}>
              {eventLabel}
            </span>
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>
              {template.teams.length} {template.teams.length === 1 ? 'squadra' : 'squadre'} · {totalRoles} {totalRoles === 1 ? 'ruolo' : 'ruoli'}
            </span>
          </div>
        )}

        <div style={{ display: 'flex', gap: '0.25rem' }} onClick={e => e.stopPropagation()}>
          <button onClick={() => { setEditingName(true); setExpanded(true); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', padding: '0.25rem 0.375rem' }} title="Modifica nome"><Pencil size={14} /></button>
          <button onClick={() => { if (confirm(`Eliminare il template "${template.name}"?`)) deleteMutation.mutate(); }} disabled={deleteMutation.isPending} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)', padding: '0.25rem 0.375rem' }} title="Elimina"><Trash2 size={14} /></button>
        </div>
      </div>

      {expanded && (
        <div style={{ padding: '0.75rem 1rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
          {template.teams.length === 0 && (
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-faint)', fontStyle: 'italic', margin: 0 }}>
              Nessuna squadra. Aggiungine una.
            </p>
          )}
          {template.teams.map(team => (
            <TeamSection key={team.id} template={template} team={team} multiTeam={multiTeam} />
          ))}
          <button
            onClick={() => addTeam.mutate()}
            disabled={addTeam.isPending}
            style={{ background: 'none', border: '1px dashed rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)', cursor: 'pointer', color: 'var(--color-text-muted)', fontSize: 'var(--text-sm)', padding: '0.5rem 0.75rem', display: 'flex', alignItems: 'center', gap: '0.35rem', width: '100%' }}
          >
            <Plus size={13} /> Aggiungi squadra
          </button>
        </div>
      )}
    </div>
  );
}

// ─── CollaboratorTemplatesSection ────────────────────────────────────────────

const sectionStyle: React.CSSProperties = {
  background: 'var(--glass-bg-card)',
  border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 'var(--radius-card)',
  padding: '1.25rem',
};

export function CollaboratorTemplatesSection() {
  const qc = useQueryClient();
  const [showNewForm, setShowNewForm] = useState(false);
  const [newForm, setNewForm] = useState({ name: '', eventType: '' as EventType | '' });

  const { data: templates = [] as unknown as CollaboratorTemplate[], isLoading } = useQuery({
    queryKey: ['collaborator-templates'],
    queryFn: async () => {
      const res = await apiGet<{ data: CollaboratorTemplate[] }>('/collaborator-templates');
      return (res as unknown as { data: CollaboratorTemplate[] }).data ?? res;
    },
  });

  const createMutation = useMutation({
    mutationFn: () => apiPost('/collaborator-templates', { name: newForm.name, eventType: newForm.eventType || null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['collaborator-templates'] });
      setNewForm({ name: '', eventType: '' });
      setShowNewForm(false);
      toast.success('Template creato');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div style={sectionStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2 style={{ fontSize: 'var(--text-base)', fontWeight: 700 }}>Template Collaboratori</h2>
        <Button size="sm" icon={<Plus size={14} />} onClick={() => setShowNewForm(v => !v)}>
          Nuovo template
        </Button>
      </div>

      <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', marginBottom: '1rem' }}>
        Definisci squadre con ruoli predefiniti da applicare rapidamente all'evento.
      </p>

      {showNewForm && (
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap', background: 'var(--glass-bg-card)', borderRadius: 'var(--radius-card)', padding: '0.75rem', marginBottom: '1rem', border: '1px dashed rgba(255,255,255,0.10)' }}>
          <div style={{ flex: 2, minWidth: '10rem' }}>
            <label style={{ display: 'block', fontSize: 'var(--text-xs)', fontWeight: 600, marginBottom: '0.25rem', color: 'var(--color-text-muted)' }}>Nome template *</label>
            <input style={inputStyle} value={newForm.name} onChange={e => setNewForm(f => ({ ...f, name: e.target.value }))} placeholder="Es. Equipe matrimonio" autoFocus onKeyDown={e => { if (e.key === 'Enter' && newForm.name.trim()) createMutation.mutate(); }} />
          </div>
          <div style={{ flex: 1, minWidth: '9rem' }}>
            <label style={{ display: 'block', fontSize: 'var(--text-xs)', fontWeight: 600, marginBottom: '0.25rem', color: 'var(--color-text-muted)' }}>Tipo evento</label>
            <select style={selectStyle} value={newForm.eventType} onChange={e => setNewForm(f => ({ ...f, eventType: e.target.value as EventType | '' }))}>
              {EVENT_TYPES.map(t => <option key={t} value={t}>{t ? EVENT_TYPE_LABELS[t] : 'Tutti gli eventi'}</option>)}
            </select>
          </div>
          <div style={{ display: 'flex', gap: '0.375rem' }}>
            <Button size="sm" onClick={() => createMutation.mutate()} loading={createMutation.isPending} disabled={!newForm.name.trim()}>Crea</Button>
            <Button size="sm" variant="ghost" onClick={() => { setShowNewForm(false); setNewForm({ name: '', eventType: '' }); }}>Annulla</Button>
          </div>
        </div>
      )}

      {isLoading ? (
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>Caricamento…</p>
      ) : templates.length === 0 ? (
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-faint)', fontStyle: 'italic' }}>Nessun template. Crea il primo.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
          {templates.map(t => <TemplateCard key={t.id} template={t} />)}
        </div>
      )}
    </div>
  );
}
